'use client';

import { ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import type { Drone } from '@/types/fleet';
import { cn } from '@/lib/utils';
import { formatDuration } from './coverageFormatters';
import { UNAVAILABLE_DRONE_STATUSES } from './coveragePresentation';
import type { MissionListStatus } from './missionDetailShared';
import type { AircraftPlan } from '@/lib/coverageProjection';

/**
 * STEP 2 - WHO TAKES THE STATION, AND IN WHAT ORDER (PR 3, S5).
 *
 * The picker this replaces was a wrap of chips whose order was CLICK order, with no way to
 * reorder and none of the three facts that decide whether a set of aircraft works. An operator
 * choosing a rotation was choosing blind: a charging dock costs roughly seven times what a swap
 * magazine costs, and nothing on screen said which was which.
 *
 * SO THE THREE FACTS ARE ON THE ROW. Endurance with its PROVENANCE, the dock, and that dock's
 * turnaround. `sampled` is the only one of the three endurance sources that is a measurement -
 * `config_model_default` is 75% of a manufacturer's figure for an airframe nobody has ever
 * characterised - and an operator planning an unattended multi-hour watch is entitled to know
 * which they are looking at.
 *
 * AN AIRCRAFT THIS PLATFORM CANNOT SIZE SAYS SO, in place, and is not silently dropped. It can
 * still be picked - the server will accept it and the rotation will fly - but the preview cannot
 * score it, and a row that went quiet about that would let an operator believe the percentage
 * beside it describes the set they chose.
 *
 * ORDER IS MOVED WITH BUTTONS, NOT DRAGGED. Drag needs a pointer, and WCAG 2.5.7 asks for a
 * single-pointer alternative to any drag-only interaction; two buttons per row IS the alternative,
 * so building drag first and retrofitting it would be the wrong way round. The rotation order is
 * also short by construction - the API caps a rotation at ten.
 */

export interface CoverageAircraftStepProps {
  drones: Drone[];
  dronesStatus: MissionListStatus;
  onRetryDrones: () => void;
  /** In rotation order. */
  memberIds: string[];
  /**
   * The aircraft the mission itself is assigned to, so its row can say where it came from.
   *
   * Presentation only - this step does not seed the rotation, `initialForm` does. A row that
   * appeared by itself with no explanation would read as a bug, which is exactly what an operator
   * would reasonably conclude about a picker that filled itself in.
   */
  assignedDroneId?: string | null;
  onToggle: (droneId: string) => void;
  onMove: (droneId: string, direction: -1 | 1) => void;
  /** What the projection made of each picked aircraft, keyed by drone id. */
  plansByDrone: ReadonlyMap<string, AircraftPlan>;
  /** Endurance provenance per drone, for the aircraft that have one. */
  enduranceSourceByDrone: ReadonlyMap<string, string | null>;
  /** Cruise-speed provenance per drone. `mission_default` means the transit is a typed number. */
  cruiseSourceByDrone: ReadonlyMap<string, string | null>;
  /** Whether the planning inputs are still arriving, or failed. */
  inputsStatus: 'loading' | 'ready' | 'error';
  onRetryInputs: () => void;
  /**
   * Whether a plan could be computed AT ALL, regardless of the aircraft.
   *
   * False when the mission has no hold point, or the battery policy on step 3 is not a usable
   * percentage. Without it those two produced an empty plan map and every row rendered exactly
   * like an aircraft this platform cannot size - an aircraft problem shown for a form problem, on
   * a step that does not contain the field at fault.
   */
  plansComputable: boolean;
  /** Why not, in the operator's words, when `plansComputable` is false. */
  notComputableReason: string | null;
}

/** How an endurance figure was arrived at, in the operator's words. */
const ENDURANCE_SOURCE_LABEL: Record<string, string> = {
  sampled: 'measured',
  config_override: 'entered',
  config_model_default: 'model default',
};

/**
 * Why an aircraft cannot be projected, as a sentence a row can carry as-is.
 *
 * EACH ONE SAYS WHAT PICKING IT COSTS, not merely that the preview cannot draw it. The first two
 * are the same missing inputs `computeReplacementTiming` refuses on, and its refusal is
 * `launch_now` - so an aircraft with no endurance model does not just go unpreviewed, it is
 * relieved the moment it reaches the station and burns the next airframe in the rotation for
 * nothing. An operator reading "cannot be planned against" would reasonably assume the cost was
 * a missing bar on a chart.
 */
const REFUSAL_SENTENCE: Record<string, string> = {
  endurance_unknown:
    'No endurance on record. It will fly, but the planner relieves it as soon as it reaches the'
    + ' station, and the estimate leaves it out',
  cruise_speed_unknown:
    'No cruise speed on record. It will fly, but the planner relieves it as soon as it reaches the'
    + ' station, and the estimate leaves it out',
  no_dock_position:
    'Its dock has no recorded position, so its transit cannot be measured and the estimate leaves'
    + ' it out',
  cannot_reach_station:
    'It cannot reach the station and return on its own reserve, so the estimate leaves it out',
};

export function CoverageAircraftStep({
  drones, dronesStatus, onRetryDrones, memberIds, assignedDroneId = null, onToggle, onMove,
  plansByDrone, enduranceSourceByDrone, cruiseSourceByDrone, inputsStatus, onRetryInputs,
  plansComputable, notComputableReason,
}: CoverageAircraftStepProps) {
  const picked = memberIds
    .map((id) => drones.find((d) => d.id === id))
    .filter((d): d is Drone => d !== undefined);
  const unpicked = drones.filter((d) => !memberIds.includes(d.id));

  /** True when the figures are in and not one picked aircraft has ever been measured. */
  const noneMeasured = picked.length > 0
    && picked.every((d) => enduranceSourceByDrone.get(d.id) !== 'sampled');

  /** What the facts column says when there are no facts to say. */
  const factsHold = inputsStatus === 'loading'
    ? 'reading…'
    : inputsStatus === 'error'
      ? 'figures unavailable'
      : !plansComputable
        ? 'not computed'
        : null;

  return (
    <div className="space-y-3">
      <div className="text-[10px] text-muted-foreground uppercase tracking-wider">
        Rotation - in the order they take the station. One aircraft is allowed: it swaps at its own
        dock, and coverage gaps while it does
      </div>

      {dronesStatus === 'loading' && (
        <span className="text-xs text-muted-foreground">Loading the fleet…</span>
      )}
      {dronesStatus === 'error' && (
        <span className="flex items-center gap-2 text-xs text-status-amber-fg">
          The fleet could not be loaded
          <button
            type="button"
            onClick={onRetryDrones}
            className="min-h-6 px-2 py-1 rounded border border-border text-primary hover:text-foreground transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
          >
            Retry
          </button>
        </span>
      )}
      {dronesStatus === 'ready' && drones.length === 0 && (
        <span className="text-xs text-muted-foreground">No aircraft in the fleet</span>
      )}

      {/* THE FACTS COULD NOT BE READ, which is not the same as an uncharacterised fleet. Said
          once, above the list, rather than repeated as "unknown" on every row - the rows would
          then look like an aircraft problem when it is a request that failed. */}
      {inputsStatus === 'error' && (
        <p className="flex flex-wrap items-center gap-2 text-xs text-status-amber-fg">
          <span className="flex items-start gap-1.5">
            <AlertTriangle className="size-3.5 shrink-0 mt-0.5" strokeWidth={1.5} aria-hidden="true" />
            Endurance and dock figures could not be read, so this rotation cannot be previewed
          </span>
          {/* NAMED FOR WHAT IT RELOADS. The fleet's own Retry can be on screen at the same time,
              and two buttons both saying "Retry" beside two different failures is a coin toss. */}
          <button
            type="button"
            onClick={onRetryInputs}
            className="min-h-6 px-2 py-1 rounded border border-border text-primary hover:text-foreground transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
          >
            Retry the figures
          </button>
        </p>
      )}

      {/* SAID ONCE, IN WORDS, WHEN IT IS TRUE OF THE WHOLE ROTATION.
          No calibration flight has ever run on this platform, so in practice almost every aircraft
          resolves to `config_model_default` - 75% of a manufacturer's maximum for an airframe
          nobody has characterised. Rendered only as a per-row parenthetical it becomes wallpaper by
          the third row, and the one place this product must LOWER an operator's confidence is the
          figure they are about to commit an unattended multi-hour watch against. */}
      {/* A form problem, not an aircraft problem, and named on the step that shows the effect. */}
      {inputsStatus === 'ready' && !plansComputable && notComputableReason !== null && (
        <p className="text-[11px] text-muted-foreground">{notComputableReason}</p>
      )}

      {inputsStatus === 'ready' && plansComputable && picked.length > 0 && noneMeasured && (
        <p className="flex items-start gap-1.5 text-[11px] text-status-amber-fg">
          <AlertTriangle className="size-3.5 shrink-0 mt-0.5" strokeWidth={1.5} aria-hidden="true" />
          No aircraft in this rotation has a measured endurance. Every figure below is 75% of the
          manufacturer&apos;s nominal for its model, not something this airframe has flown.
        </p>
      )}

      {picked.length > 0 && (
        <ol className="space-y-1" role="list" aria-label="Rotation order">
          {picked.map((d, i) => {
            const plan = plansByDrone.get(d.id);
            const source = enduranceSourceByDrone.get(d.id) ?? null;
            const unavailable = UNAVAILABLE_DRONE_STATUSES.has(d.status);
            return (
              <li
                key={d.id}
                data-testid="rotation-pick"
                className="flex flex-wrap items-center gap-2 rounded border border-border bg-background px-2 py-1.5"
              >
                <span className="text-[11px] font-mono tabular-nums text-primary w-4 shrink-0">
                  {i + 1}.
                </span>
                <span className="text-[11px] uppercase tracking-wider text-foreground">
                  {d.callsign ?? d.name}
                </span>
                <span className={cn(
                  'text-[10px] uppercase tracking-wider',
                  unavailable ? 'text-status-amber-fg' : 'text-muted-foreground',
                )}
                >
                  {d.status.toUpperCase()}
                </span>
                {/* WHERE THIS ROW CAME FROM. The mission's own aircraft is seeded into the
                    rotation, and a picker that fills itself in without saying why is a picker an
                    operator distrusts.
                    Muted rather than a status colour: this is provenance, not a verdict, and in
                    this palette a colour on a row is a claim about readiness. AFTER the status and
                    in parentheses for the same reason - two adjacent 10 px uppercase muted tokens
                    read as one two-part status field, and when the aircraft is unavailable the real
                    status turns amber beside a provenance that does not. */}
                {assignedDroneId !== null && d.id === assignedDroneId && (
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    (mission aircraft)
                  </span>
                )}

                {/* THE THREE FACTS THAT DECIDE THE ANSWER.
                    Monospace because they are telemetry values, and the two DURATIONS are
                    `text-foreground` because they are what the operator came to this step for -
                    they ranked below a raw status enum in the first cut.

                    THE INPUT AND THE DERIVED FIGURE ARE SEPARATE. `on station` is endurance minus
                    both transits minus the planner's margin, so the transit's own source is in it
                    too - hanging "(measured)" off it made a confidence claim about a figure whose
                    other half may be the mission's typed speed. The provenance now sits on the
                    endurance, which is the thing it is actually about, and never appears beside a
                    dash. */}
                <span className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] font-mono tabular-nums">
                  {factsHold !== null ? (
                    // The banner above says WHY once. Repeating "unknown" per row would read as an
                    // aircraft problem when it is a request that has not answered, or a blank field
                    // on a different step.
                    <span className="text-muted-foreground">{factsHold}</span>
                  ) : (
                    <>
                      <span className="text-muted-foreground">
                        {plan?.enduranceMinutesAtFull != null
                          ? `${Math.round(plan.enduranceMinutesAtFull)} min endurance`
                          : 'no endurance'}
                        {plan?.enduranceMinutesAtFull != null && source !== null && (
                          <span className={cn(
                            'ml-1 normal-case',
                            // MEASURED IS THE EXCEPTION, and must not look like one of three
                            // equals. No calibration flight has ever run on this platform, so
                            // nearly every row is a manufacturer's nominal scaled by 0.75.
                            source === 'sampled' ? 'text-status-green-fg' : 'text-status-amber-fg',
                          )}
                          >
                            {ENDURANCE_SOURCE_LABEL[source] ?? source}
                          </span>
                        )}
                      </span>
                      {plan?.dwellSeconds != null && (
                        <span className="text-foreground">
                          {formatDuration(plan.dwellSeconds)} on station
                          {/* THE TRANSIT'S OWN PROVENANCE. This figure is endurance minus both
                              transits, and a transit is distance over the cruise speed - so a
                              `mission_default` speed is a first-order term in it. Marked, because
                              editing the mission's planned speed moves this number and nothing
                              else on the row would say why. */}
                          {cruiseSourceByDrone.get(d.id) === 'mission_default' && (
                            <span className="ml-1 normal-case text-status-amber-fg">
                              at the mission&apos;s planned speed
                            </span>
                          )}
                        </span>
                      )}
                      {plan?.dockName != null && (
                        <span className="text-muted-foreground uppercase">
                          {plan.dockName}
                          <span className={plan.isSwapDock ? 'text-status-green-fg' : 'text-status-amber-fg'}>
                            {' '}{plan.isSwapDock ? 'SWAP' : 'CHARGE'}
                          </span>
                          {plan.turnaroundSeconds != null && (
                            <span className="text-foreground"> {formatDuration(plan.turnaroundSeconds)}</span>
                          )}
                        </span>
                      )}
                    </>
                  )}
                </span>

                <span className="ml-auto flex items-center gap-1">
                  <button
                    type="button"
                    aria-label={`Move ${d.callsign ?? d.name} earlier in the rotation`}
                    disabled={i === 0}
                    onClick={() => onMove(d.id, -1)}
                    className="min-h-6 min-w-6 grid place-items-center rounded border border-border text-muted-foreground hover:text-foreground disabled:opacity-40 outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                  >
                    <ChevronUp className="size-3.5" strokeWidth={1.5} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    aria-label={`Move ${d.callsign ?? d.name} later in the rotation`}
                    disabled={i === picked.length - 1}
                    onClick={() => onMove(d.id, 1)}
                    className="min-h-6 min-w-6 grid place-items-center rounded border border-border text-muted-foreground hover:text-foreground disabled:opacity-40 outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                  >
                    <ChevronDown className="size-3.5" strokeWidth={1.5} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    aria-label={`Remove ${d.callsign ?? d.name} from the rotation`}
                    onClick={() => onToggle(d.id)}
                    className="min-h-6 px-2 rounded border border-border text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                  >
                    Remove
                  </button>
                </span>

                {/* THE ROW SAYS WHEN IT CANNOT BE PLANNED AGAINST. It can still fly - the server
                    accepts it - but the percentage beside the chart will not describe it, and a
                    row that stayed quiet would let an operator believe otherwise. */}
                {plan?.refusal != null && inputsStatus === 'ready' && plansComputable && (
                  <span className="basis-full flex items-start gap-1.5 text-[10px] text-status-amber-fg">
                    <AlertTriangle className="size-3 shrink-0 mt-0.5" strokeWidth={1.5} aria-hidden="true" />
                    {REFUSAL_SENTENCE[plan.refusal] ?? 'This aircraft cannot be planned against'}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      )}

      {unpicked.length > 0 && (
        <div>
          <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">
            Add to the rotation
          </div>
          <div className="flex flex-wrap gap-2">
            {unpicked.map((d) => (
              <button
                key={d.id}
                type="button"
                aria-pressed={false}
                onClick={() => onToggle(d.id)}
                className={cn(
                  'min-h-6 px-2 py-1 rounded border text-[11px] uppercase tracking-wider transition-colors',
                  'outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
                  'border-border bg-background text-muted-foreground hover:text-foreground',
                )}
              >
                {d.callsign ?? d.name}
                <span className={cn(
                  UNAVAILABLE_DRONE_STATUSES.has(d.status)
                    ? 'text-status-amber-fg'
                    : 'text-muted-foreground',
                )}
                > · {d.status.toUpperCase()}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
